import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { navLinks } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection, usePrefersReducedMotion, useActiveSection } from '@hooks';
import { Menu, ThemeToggle } from '@components';
import { IconLogo, IconHex } from '@components/icons';

const StyledHeader = styled.header`
  ${({ theme }) => theme.mixins.flexBetween};
  position: fixed;
  top: 0;
  z-index: 11;
  padding: 0px 50px;
  width: 100%;
  height: var(--nav-scroll-height);
  background-color: var(--bg-transparent);
  filter: none !important;
  pointer-events: auto !important;
  user-select: auto !important;
  transition: var(--transition-slow);

  @media (max-width: 1080px) {
    padding: 0 40px;
  }
  @media (max-width: 768px) {
    padding: 0 25px;
  }

  /* The blur belongs to the scrolled state only. At the top the bar is fully
     transparent, so a backdrop-filter there has nothing to separate — all it
     does is blur the Backdrop layers behind it, and blur(10px) erases a 1px
     dot lattice completely. That was the seam across the top of the page. */
  ${props =>
    !props.scrolledToTop &&
    css`
      background-color: var(--bg-translucent);
      backdrop-filter: blur(10px);
      box-shadow: 0 10px 30px -10px var(--shadow);
    `};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.flexBetween};
  position: relative;
  width: 100%;
  color: var(--text-emphasis);
  font-family: var(--font-sans);
  z-index: 12;

  .logo {
    ${({ theme }) => theme.mixins.flexCenter};

    a {
      color: var(--text);
      width: 42px;
      height: 42px;
      position: relative;
      z-index: 1;

      .hex-container {
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        @media (prefers-reduced-motion: no-preference) {
          transition: var(--transition);
        }
      }

      .logo-container {
        position: relative;
        z-index: 1;
        svg {
          fill: none;
          user-select: none;
          @media (prefers-reduced-motion: no-preference) {
            transition: var(--transition);
          }
          polygon {
            fill: var(--bg);
          }
        }
      }

      &:hover,
      &:focus-visible {
        outline: 0;
        color: var(--accent);
        transform: translate(-3px, -5.2px);
        .hex-container {
          transform: translate(3px, 3.9px);
        }
      }
    }
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }

  ol {
    ${({ theme }) => theme.mixins.flexBetween};
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      margin: 0 5px;
      position: relative;
      font-size: var(--fz-md);

      /* Same optical correction the buttons use, so the link labels and the
         Resume button's label share one cap-height line rather than sitting
         ~0.17em apart. */
      a {
        padding: calc(10px + var(--optical)) 10px calc(10px - var(--optical));

        /* The section currently under the header. Colour only — no underline
           or weight change, so the row's rhythm and width stay put as you
           scroll and the highlight moves. */
        &.active {
          color: var(--accent);
        }
      }
    }
  }

  /* Sits between the links and the Resume button, and is deliberately not a
     bordered control: two bordered objects side by side read as two buttons of
     equal weight, and this one is a preference rather than a destination.

     No margin of its own. The button is a 38px circle around an 18px icon, so
     it already carries 10px of its own air on each side — the same as a nav
     link's horizontal padding. Adding a margin on top of that only opened a
     gap on the left. */
  .resume-button {
    ${({ theme }) => theme.mixins.smallButton};
    margin-left: 15px;
  }
`;

/* Carries the header's state across client-side navigations. Each page mounts
   its own Nav, so without this the header on the new page appears already in
   the top-of-page state — transparent, no shadow — and the change from the
   scrolled state has nothing to animate from. Module scope, so it resets on a
   real page load, which is right: there is no previous page to carry over. */
let lastScrolledToTop = true;

const Nav = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome);
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(lastScrolledToTop);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Only the sections that have a nav link. The hero has none, so nothing is
  // lit while you are on it.
  const sectionIds = navLinks.map(({ url }) => url.split('#')[1]).filter(Boolean);
  const activeSection = useActiveSection(sectionIds, isHome);

  const handleScroll = () => {
    const atTop = window.pageYOffset < 50;
    lastScrolledToTop = atTop;
    setScrolledToTop(atTop);
  };

  useEffect(() => {
    // A frame after mount, not during it: the header has to paint in the state
    // it inherited from the previous page before moving to this one's, or the
    // browser has nothing to interpolate and the change is instant.
    const raf = window.requestAnimationFrame(handleScroll);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prefersReducedMotion]);

  const timeout = isHome ? loaderDelay : 0;
  const fadeClass = isHome ? 'fade' : '';
  const fadeDownClass = isHome ? 'fadedown' : '';

  // Colour only, and only on the home page — a subpage has no section under
  // the header to be "in".
  const linkProps = url =>
    isHome && activeSection && url === `/#${activeSection}`
      ? { className: 'active', 'aria-current': 'location' }
      : {};

  // Always a Link, never a bare <a href="/">. The template used a raw anchor on
  // the home page so that clicking the logo forced a full page load and
  // replayed the hexagon intro; here the logo is only ever navigation.
  const Logo = (
    <div className="logo" tabIndex="-1">
      <Link to="/#hero" aria-label="home">
        <div className="hex-container">
          <IconHex />
        </div>
        <div className="logo-container">
          <IconLogo />
        </div>
      </Link>
    </div>
  );

  const ResumeLink = (
    <a className="resume-button" href="/resume.pdf" target="_blank" rel="noopener noreferrer">
      Resume
    </a>
  );

  return (
    <StyledHeader scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <StyledNav>
        {prefersReducedMotion ? (
          <>
            {Logo}

            <StyledLinks>
              <ol>
                {navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <li key={i}>
                      <Link to={url} {...linkProps(url)}>
                        {name}
                      </Link>
                    </li>
                  ))}
              </ol>
              <ThemeToggle className="theme-toggle" />
              <div>{ResumeLink}</div>
            </StyledLinks>

            <Menu />
          </>
        ) : (
          <>
            {/* Logo goes in directly rather than wrapped in a fragment.
                CSSTransition reaches for its child through findDOMNode, and a
                fragment has no host node of its own for that to resolve to. */}
            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  {Logo}
                </CSSTransition>
              )}
            </TransitionGroup>

            <StyledLinks>
              <ol>
                <TransitionGroup component={null}>
                  {isMounted &&
                    navLinks &&
                    navLinks.map(({ url, name }, i) => (
                      <CSSTransition key={i} classNames={fadeDownClass} timeout={timeout}>
                        <li key={i} style={{ transitionDelay: `${isHome ? i * 100 : 0}ms` }}>
                          <Link to={url} {...linkProps(url)}>
                            {name}
                          </Link>
                        </li>
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </ol>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                    <div style={{ transitionDelay: `${isHome ? navLinks.length * 100 : 0}ms` }}>
                      <ThemeToggle className="theme-toggle" />
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                    <div
                      style={{ transitionDelay: `${isHome ? (navLinks.length + 1) * 100 : 0}ms` }}>
                      {ResumeLink}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </StyledLinks>

            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <Menu />
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        )}
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
};

export default Nav;
